import { Box, Slider, Stack, Typography } from '@mui/material'
import Head from 'next/head'
import { ImgHTMLAttributes, useCallback, useEffect, useState } from 'react'
import { useSwipeable } from 'react-swipeable'
import { IconButton } from 'utoniq-ui'

import { Resource } from '@/clients/utoniq-core/schema'
import { TheoPlayerImageView } from '@/components/organisms/resourcePlayer/TheoPlayerView'
import { styles } from '@/components/styles'
import { isMobileDevice } from '@/components/styles'
import { useMessage, useTranslation } from '@/core'
import { useDRMPlayer } from '@/core/resources/useDRMPlayer'

import { SwipeBackdrop } from './SwipeBackdrop'

/**
 * @description DRMMagazinePlayer
 * DRM化されたマガジンアイテムを再生するのに用いる
 * 動画化されたコンテンツの再生位置を「2秒ずつ」スキップしてページ送りを再現する
 */

type Props = {
  resource: Resource
  open: boolean
  onClose: () => void
} & Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'resource'>

export const DRMMagazinePlayer = ({ resource, open, onClose }: Props) => {
  const { showError } = useMessage()
  const { t } = useTranslation()
  const { ref, player, accessToken } = useDRMPlayer({
    source: {
      dashUri: resource?.dashUri || '',
      hlsUri: resource?.hlsUri || '',
    },
    opts: {
      mutedAutoplay: 'all',
    },
    isMagazine: true,
  })
  const [isLastPage, setIsLastPage] = useState(false)

  useEffect(() => {
    if (!player || !open) return
    if (!accessToken) showError(t('drmAuthorizingError'))

    // THEOPlayer認証後、事前にDRMリソース読み取りを行う
    player.play()
    player.pause()
    // HACK: 起動時に動画の再生位置を3秒に設定する
    player.currentTime = 3
    setIsLastPage(false)
  }, [accessToken, open])

  /**
   * 次のページに遷移する処理
   */
  const handleNext = useCallback(() => {
    //  初期化前はスキップ
    if (!player || !player.duration || !player.currentTime) return

    const currentPage = Math.floor(((player?.currentTime ?? 3) - 1) / 2)
    const totalPage = Math.floor(((player?.duration ?? 0) - 2) / 2)

    /** 再生位置を現在+2秒に変更する */
    player.currentTime += 2

    // HACK: 最後のページ付近でTHEOPlayerのシークが実行されないことがあるため、play()を実行することで最後のページを表示する
    if (currentPage === totalPage - 1) {
      player.play()
      setIsLastPage(true)

      const time = setTimeout(() => {
        player.pause()
        clearTimeout(time)
      }, 1200)
    }
  }, [player])

  /**
   * 前のページに遷移する処理
   */
  const handlePrev = useCallback(() => {
    // 初期化前はスキップ
    if (!player) return

    player.pause()
    setIsLastPage(false)

    /** 再生位置を現在-2秒に変更する */
    player.currentTime = Math.floor(player.currentTime) - 2
    // HACK: 再生位置を奇数に設定するため、時間操作後の現在時間が偶数になった場合は1秒足す
    if (player.currentTime % 2 === 0) player.currentTime++
  }, [player])

  /**
   * 指定したページにジャンプする処理
   */
  const handleJump = useCallback(
    (page: number) => {
      if (!player || !player.duration) return

      player.pause()
      setIsLastPage(false)

      const targetTime = page * 2 + 1
      if (targetTime > player.duration) {
        player.currentTime = Math.floor(player.duration)
        return
      }

      player.currentTime = targetTime
    },
    [player]
  )

  if (!accessToken) return null

  return (
    <>
      {open && (
        <Head>
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1.0, viewport-fit=cover, minimum-scale=1.0, maximum-scale=2.0"
          />
        </Head>
      )}
      <Box
        sx={{
          bgcolor: 'background.default',
          width: '100%',
          position: 'absolute',
          zIndex: 1,
          top: 0,
          left: 0,
          right: 0,
          maxWidth: 600,
          margin: 'auto',
          minHeight: 'fit-content',
          height: '100%',
          display: 'grid',
          placeItems: 'start center',
          opacity: open && !!player?.duration ? 1 : 0,
          pointerEvents: open ? 'initial' : 'none',
        }}
      >
        {open && (
          <MagazineUI
            onPrev={handlePrev}
            onNext={handleNext}
            onJump={handleJump}
            currentPage={Math.floor(((player?.currentTime ?? 3) - 1) / 2)}
            totalPage={Math.floor(((player?.duration ?? 0) - 2) / 2)}
            onClose={onClose}
            isLastPage={isLastPage}
          />
        )}
        <TheoPlayerImageView ref={ref} playable={!!player?.duration} />
      </Box>
    </>
  )
}

type MagazineUIProps = {
  /** 前のページに戻る処理 */
  onPrev: () => void
  /** 次のページに進む処理 */
  onNext: () => void
  /** 指定したページに遷移する処理 */
  onJump: (page: number) => void
  /** 現在表示しているページ数 */
  currentPage: number
  /** マガジンのページ総数 */
  totalPage: number
  onClose: () => void
  /** 最終ページかどうか */
  isLastPage: boolean
}

/** マガジンプレーヤーを操作するためのUI */
const MagazineUI = ({
  onPrev,
  onNext,
  onJump,
  currentPage,
  totalPage,
  onClose,
  isLastPage,
}: MagazineUIProps) => {
  const [show, setShow] = useState(true)
  const [value, setValue] = useState(1)
  const [showBackdrop, setShowBackdrop] = useState(true)

  const handlers = useSwipeable({
    onSwipedLeft: () => {
      if (isLastPage) return
      setShowBackdrop(false)
      onNext()
      setValue(state => state + 1)
    },
    onSwipedRight: () => {
      if (currentPage <= 1) return
      setShowBackdrop(false)
      onPrev()
      setValue(state => state - 1)
    },
    onTap: ({ event }) => {
      if (event.target instanceof HTMLButtonElement) return
      if (event.target instanceof HTMLSpanElement) return
      setShowBackdrop(false)
      setShow(state => !state)
    },
    // PCのときはマウス操作できるようにする
    trackMouse: !isMobileDevice(),
  })

  return (
    <>
      <SwipeBackdrop open={showBackdrop} />
      {show && (
        <Box
          sx={{
            pt: styles.safeAreaTop,
            position: 'absolute',
            zIndex: 3,
            top: 20,
            left: 20,
          }}
        >
          <IconButton.Close
            onClick={onClose}
            sx={{
              bgcolor: 'grey.50',
            }}
          />
        </Box>
      )}
      <Box
        sx={{
          width: '100%',
          zIndex: 2,
          position: 'absolute',
          top: 0,
          left: 0,
          height: '100%',
        }}
        {...handlers}
      >
        {show && (
          <>
            <Stack
              sx={{
                position: 'absolute',
                width: '100%',
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                pb: styles.safeAreaBottom,
              }}
            >
              <Box
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.6)',
                  padding: '4px 12px',
                  width: '100px',
                  borderRadius: '4px',
                }}
              >
                <Typography variant="subtitle1" textAlign="center">
                  {isLastPage ? totalPage : currentPage} / {totalPage}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: 'grid',
                  placeItems: 'center',
                  bgcolor: 'grey.100',
                  width: '100%',
                  padding: '0 40px',
                }}
              >
                <Slider
                  min={1}
                  max={totalPage}
                  value={value}
                  onChange={(_, v) => {
                    setShowBackdrop(false)
                    if (typeof v === 'number') {
                      setValue(v)
                      onJump(v)
                    }
                  }}
                  onChangeCommitted={(_, v) => {
                    if (typeof v === 'number') onJump(v)
                  }}
                />
              </Box>
            </Stack>
          </>
        )}
      </Box>
    </>
  )
}
